import { zilaSlab } from 'assets/theme/theme';
import {
	Alert,
	Box, Card, CardActions, CardContent, CircularProgress, Stack, Typography,
} from '@mui/material';
import { backoffBackground, logo } from 'assets/images';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, DefaultButton, TitleMedium } from 'common/presentation/components';
import { Link } from 'react-router-dom';
import { Key } from '@mui/icons-material';
import TextField from 'common/presentation/components/text-field/TextField';
import AppLinks from 'assets/applinks.routes';
import useAuthBackoffice from '../provider/auth-backoffice.provider';
import { makeRegisterValidation } from 'features/authentication/presentation/builder/authentication-factory';

const ResetPasswordPage = () => {
	const { t } = useTranslation('translations');
	const {
		resetPassword, loading, error, setError,
	} = useAuthBackoffice();

	useEffect(() => {
		setError('');
	}, []);

	const queryParameters = new URLSearchParams(window.location.search);
	const email = queryParameters.get('email') || '';
	const token = queryParameters.get('token') || '';

	const [password, setPassword] = React.useState({
		password: '',
		repeatPassword: '',
	});

	const [passwordError, setPasswordError] = React.useState({
		password: '',
		repeatPassword: '',
	});

	const [success, setSuccess] = React.useState('');

	const validation = makeRegisterValidation();

	const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const formData = { ...password };
		const passwordErrorV = validation.validate('password', formData);
		const repeatPasswordErrorV = validation.validate('repeatPassword', formData);
		const isFormValid = !(!!passwordErrorV || !!repeatPasswordErrorV);
		setPasswordError({
			password: passwordErrorV,
			repeatPassword: repeatPasswordErrorV,
		});
		if (isFormValid) {
			resetPassword(email, password.password, token).then((result) => {
				if (result) {
					setPassword({
						password: '',
						repeatPassword: '',
					});
					setSuccess(result.toString());
				}
			});
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		setPassword((prev) => ({ ...prev, [event.target.name]: event.target.value }));
		setPasswordError((prev) => ({ ...prev, [event.target.name]: '' }));
	};

	return (
		<Box sx={{
			backgroundImage: `url(${backoffBackground})`,
			backgroundRepeat: 'no-repeat',
			backgroundSize: 'cover',
			height: '100vh',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		}}
		>
			<form onSubmit={onSubmit} autoComplete="off">
				<Card sx={{
					maxWidth: 410, minHeight: 450, m: 'auto', borderRadius: 4, boxShadow: 'none',
				}}
				>
					<CardContent sx={{ textAlign: 'center', m: 2 }}>
						<Box sx={{ mb: 2 }}>
							<img src={logo} alt="Logo da volt" />
						</Box>
						<TitleMedium sx={{
							fontFamily: zilaSlab, fontWeight: 500, fontSize: 20, mb: 1,
						}}
						>
							{t('change_password')}
						</TitleMedium>
						<BodyText sx={{
							fontSize: 14, mb: 4, paddingInline: 6, fontFamily: zilaSlab,
						}}
						>
							{t('change_password_info')}
						</BodyText>
						<Stack>
							<Typography color="red">
								{error}
							</Typography>
							{success && (
								<Alert severity="success" sx={{ mb: 2 }}>
									{success}
								</Alert>
							)}
						</Stack>
						<TextField
							sx={{
								borderRadius: 2, mb: 2, height: 50, fontFamily: zilaSlab,
							}}
							name="password"
							type="password"
							error={!!passwordError.password}
							helperText={passwordError.password}
							placeholder={t('password')}
							fullWidth
							value={password.password}
							onChange={handleChange}
							startIcon={<Key />}
						/>
						<TextField
							sx={{
								borderRadius: 2, mb: 2, height: 50, fontFamily: zilaSlab,
							}}
							name="repeatPassword"
							type="password"
							error={!!passwordError.repeatPassword}
							helperText={passwordError.repeatPassword}
							placeholder={t('repeat_the_password')}
							fullWidth
							value={password.repeatPassword}
							onChange={handleChange}
							startIcon={<Key />}
						/>
						{success && (
							<BodyText>
								<Link style={{ color: '#B9C92B' }} to={AppLinks.LOGIN_BACKOFFICE}>{t('back_to_login')}</Link>
							</BodyText>
						)}
					</CardContent>
					<CardActions sx={{ display: 'flex', justifyContent: 'center' }}>
						<DefaultButton
							disabled={loading || !!success}
							sx={{
								borderRadius: 2, width: 250, textTransform: 'capitalize', mb: 2, height: 50, fontSize: 16, fontFamily: zilaSlab,
							}}
							type="submit"
						>
							{loading && <CircularProgress size={30} />}
							{!loading && t('recover_password')}
						</DefaultButton>
					</CardActions>
				</Card>
			</form>
		</Box>
	);
};

export default ResetPasswordPage;
